
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, PieChart, Pie, Cell, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { RequirementsData, StateRequirement } from "./types";

const RequirementsFeedbackAnalytics = () => {
  const { data: requirementsData, isLoading } = useQuery({
    queryKey: ["requirementsFeedbackAnalytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_registrations")
        .select(`
          id,
          overall_rating,
          recommend_rating,
          expectation_rating,
          learning_style_rating,
          materials_rating,
          courses (
            id,
            title,
            state_requirements
          )
        `)
        .not("feedback", "is", null);

      if (error) throw error;
      
      // Extract state requirements data
      const requirements = {
        'Positive Behavioral Intervention': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Reading Preparation': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Key Warning Signs': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Suicide Prevention': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'English Learners': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'American Indian History': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Cultural Competency': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'No Specific Requirement': { count: 0, ratings: [] as number[], courses: new Set<string>() }
      };
      
      // Cultural competency subtypes
      const culturalCompetencyTypes = {
        'Racial Competency': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Religion': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Systemic Racism': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Gender Identity': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Sexual Orientation': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Language Diversity': { count: 0, ratings: [] as number[], courses: new Set<string>() },
        'Disabilities': { count: 0, ratings: [] as number[], courses: new Set<string>() }
      };
      
      // Process each feedback entry
      data.forEach(item => {
        if (!item.courses?.state_requirements) {
          requirements['No Specific Requirement'].count++;
          if (item.overall_rating) {
            requirements['No Specific Requirement'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['No Specific Requirement'].courses.add(item.courses.id);
          }
          return;
        }
        
        const reqs = item.courses.state_requirements as StateRequirement;
        let requirementFound = false;
        
        // Check for behavioral intervention
        if (reqs.behavioralIntervention) {
          requirements['Positive Behavioral Intervention'].count++;
          if (item.overall_rating) {
            requirements['Positive Behavioral Intervention'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['Positive Behavioral Intervention'].courses.add(item.courses.id);
          }
          requirementFound = true;
        }
        
        // Check for reading preparation
        if (reqs.readingPreparation) {
          requirements['Reading Preparation'].count++;
          if (item.overall_rating) {
            requirements['Reading Preparation'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['Reading Preparation'].courses.add(item.courses.id);
          }
          requirementFound = true;
        }
        
        // Check for key warning signs
        if (reqs.keyWarningSigns) {
          requirements['Key Warning Signs'].count++;
          if (item.overall_rating) {
            requirements['Key Warning Signs'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['Key Warning Signs'].courses.add(item.courses.id);
          }
          requirementFound = true;
        }
        
        // Check for suicide prevention
        if (reqs.suicidePrevention) {
          requirements['Suicide Prevention'].count++;
          if (item.overall_rating) {
            requirements['Suicide Prevention'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['Suicide Prevention'].courses.add(item.courses.id);
          }
          requirementFound = true;
        }
        
        // Check for English learners
        if (reqs.englishLearners) {
          requirements['English Learners'].count++;
          if (item.overall_rating) {
            requirements['English Learners'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['English Learners'].courses.add(item.courses.id);
          }
          requirementFound = true;
        }
        
        // Check for American Indian history
        if (reqs.americanIndianHistory) {
          requirements['American Indian History'].count++;
          if (item.overall_rating) {
            requirements['American Indian History'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['American Indian History'].courses.add(item.courses.id);
          }
          requirementFound = true;
        }
        
        // Check for cultural competency
        if (reqs.culturalCompetency?.enabled) {
          requirements['Cultural Competency'].count++;
          if (item.overall_rating) {
            requirements['Cultural Competency'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['Cultural Competency'].courses.add(item.courses.id);
          }
          requirementFound = true;
          
          // Process cultural competency subtypes
          if (reqs.culturalCompetency.racial) {
            culturalCompetencyTypes['Racial Competency'].count++;
            if (item.overall_rating) {
              culturalCompetencyTypes['Racial Competency'].ratings.push(item.overall_rating);
            }
            if (item.courses?.id) {
              culturalCompetencyTypes['Racial Competency'].courses.add(item.courses.id);
            }
          }
          
          if (reqs.culturalCompetency.religion) {
            culturalCompetencyTypes['Religion'].count++;
            if (item.overall_rating) {
              culturalCompetencyTypes['Religion'].ratings.push(item.overall_rating);
            }
            if (item.courses?.id) {
              culturalCompetencyTypes['Religion'].courses.add(item.courses.id);
            }
          }
          
          if (reqs.culturalCompetency.systemicRacism) {
            culturalCompetencyTypes['Systemic Racism'].count++;
            if (item.overall_rating) {
              culturalCompetencyTypes['Systemic Racism'].ratings.push(item.overall_rating);
            }
            if (item.courses?.id) {
              culturalCompetencyTypes['Systemic Racism'].courses.add(item.courses.id);
            }
          }
          
          if (reqs.culturalCompetency.genderIdentity) {
            culturalCompetencyTypes['Gender Identity'].count++;
            if (item.overall_rating) {
              culturalCompetencyTypes['Gender Identity'].ratings.push(item.overall_rating);
            }
            if (item.courses?.id) {
              culturalCompetencyTypes['Gender Identity'].courses.add(item.courses.id);
            }
          }
          
          if (reqs.culturalCompetency.sexualOrientation) {
            culturalCompetencyTypes['Sexual Orientation'].count++;
            if (item.overall_rating) {
              culturalCompetencyTypes['Sexual Orientation'].ratings.push(item.overall_rating);
            }
            if (item.courses?.id) {
              culturalCompetencyTypes['Sexual Orientation'].courses.add(item.courses.id);
            }
          }
          
          if (reqs.culturalCompetency.languageDiversity) {
            culturalCompetencyTypes['Language Diversity'].count++;
            if (item.overall_rating) {
              culturalCompetencyTypes['Language Diversity'].ratings.push(item.overall_rating);
            }
            if (item.courses?.id) {
              culturalCompetencyTypes['Language Diversity'].courses.add(item.courses.id);
            }
          }
          
          if (reqs.culturalCompetency.disabilities) {
            culturalCompetencyTypes['Disabilities'].count++;
            if (item.overall_rating) {
              culturalCompetencyTypes['Disabilities'].ratings.push(item.overall_rating);
            }
            if (item.courses?.id) {
              culturalCompetencyTypes['Disabilities'].courses.add(item.courses.id);
            }
          }
        }
        
        // If no requirement was found, count as "No Specific Requirement"
        if (!requirementFound) {
          requirements['No Specific Requirement'].count++;
          if (item.overall_rating) {
            requirements['No Specific Requirement'].ratings.push(item.overall_rating);
          }
          if (item.courses?.id) {
            requirements['No Specific Requirement'].courses.add(item.courses.id);
          }
        }
      });
      
      // Calculate averages and format the data
      const requirementsData = Object.entries(requirements).map(([name, data]) => ({
        name,
        count: data.count,
        avgRating: data.ratings.length > 0 ? data.ratings.reduce((sum, val) => sum + val, 0) / data.ratings.length : 0,
        courseCount: data.courses.size
      }));
      
      const culturalCompetencyData = Object.entries(culturalCompetencyTypes).map(([name, data]) => ({
        name,
        count: data.count,
        avgRating: data.ratings.length > 0 ? data.ratings.reduce((sum, val) => sum + val, 0) / data.ratings.length : 0,
        courseCount: data.courses.size
      }));
      
      return {
        requirements: requirementsData,
        culturalCompetency: culturalCompetencyData
      } as RequirementsData;
    },
  });
  
  // Colors for charts
  const COLORS = ['#004161', '#cfb9a2', '#0f8f8c', '#b1c3cf', '#f1eceb', '#005b8c', '#0a7d7a', '#c0a68e'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-muted-foreground">Loading state requirements analytics...</p>
      </div>
    );
  }

  // Move all useMemo hooks outside of any conditional blocks and always call them
  // Create empty arrays for when data isn't available yet
  const emptyArray: any[] = [];
  
  // Define chart data outside any conditions to ensure hook call consistency
  const requirementsDistribution = requirementsData?.requirements
    ? requirementsData.requirements
        .filter(req => req.count > 0)
        .map(req => ({
          name: req.name,
          value: req.count
        }))
    : emptyArray;
  
  const requirementsRatings = requirementsData?.requirements
    ? requirementsData.requirements
        .filter(req => req.count >= 3) // Only include requirements with at least 3 ratings
        .sort((a, b) => b.avgRating - a.avgRating)
        .map(req => ({
          name: req.name,
          rating: parseFloat(req.avgRating.toFixed(1))
        }))
    : emptyArray;
  
  const culturalCompetencyDistribution = requirementsData?.culturalCompetency
    ? requirementsData.culturalCompetency
        .filter(cc => cc.count > 0)
        .map(cc => ({
          name: cc.name,
          value: cc.count
        }))
    : emptyArray;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-primary">Feedback by State Requirement</CardTitle>
            <CardDescription className="font-subtitle">
              Distribution of feedback by state requirement
            </CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={requirementsDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name.length > 20 ? name.substring(0, 20) + '...' : name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {requirementsDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} feedback submissions`, '']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-primary">State Requirement Ratings</CardTitle>
            <CardDescription className="font-subtitle">
              Average ratings by state requirement
            </CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={requirementsRatings}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 4]} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  tick={{ fontSize: 12 }}
                  width={100}
                />
                <Tooltip formatter={(value) => [`${value}/4`, 'Average Rating']} />
                <Bar dataKey="rating" fill="#004161">
                  {requirementsRatings.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-primary">Cultural Competency Breakdown</CardTitle>
          <CardDescription className="font-subtitle">
            Distribution of cultural competency subtypes
          </CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={culturalCompetencyDistribution}
              margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#0f8f8c" name="Feedback Count">
                {culturalCompetencyDistribution.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-primary">State Requirements Detail</CardTitle>
          <CardDescription className="font-subtitle">
            Feedback metrics by state requirement
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-section">Requirement</th>
                  <th className="text-left p-2 font-section">Course Count</th>
                  <th className="text-left p-2 font-section">Feedback Count</th>
                  <th className="text-left p-2 font-section">Avg. Rating</th>
                </tr>
              </thead>
              <tbody>
                {requirementsData?.requirements
                  ? requirementsData.requirements
                      .sort((a, b) => b.count - a.count)
                      .map(req => (
                        <tr key={req.name} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-body">{req.name}</td>
                          <td className="p-2 font-body">{req.courseCount}</td>
                          <td className="p-2 font-body">{req.count}</td>
                          <td className="p-2 font-body">
                            {req.count > 0 ? 
                              <div className="flex items-center">
                                <span className="mr-2">{req.avgRating.toFixed(1)}/4</span>
                                <div className="flex">
                                  {[...Array(4)].map((_, i) => (
                                    <span 
                                      key={i} 
                                      className={`text-xs ${i < Math.round(req.avgRating) ? 'text-amber-400' : 'text-gray-300'}`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div> : 
                              'No ratings'
                            }
                          </td>
                        </tr>
                      ))
                  : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RequirementsFeedbackAnalytics;
